// Vỏ app (N2): thanh trên + màn đang chọn (tải lười) + toast + ⏱. Giữ nhãn đang chọn cho TOÀN web (QĐ-H50).
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { type AppUser, laAdmin } from "../api/appUser";
import { useNen, useTuCapNhatNen } from "../api/nen";
import { guiDiemDoLenServer } from "../api/perfSink";
import type { DongNhan } from "../api/voApp";
import { bung, docCauHinh } from "../core/nen";
import { type ChonNhan, chonNhanKhoiDau, docDoTuoi, hopNhan, type KhoaMenu, soDoMenu, TAT_CA } from "../core/voApp";
import { homNayVN } from "../core/ngay";
import { ghiDiemDo } from "../core/perf";
import { PerfPanel } from "../ui/PerfPanel";
import { RaoLoi } from "../ui/RaoLoi";
import { Toasts } from "../ui/Toasts";
import { MAN, taiTruocCacMan } from "./manHinh";
import { dieuHuong, useDuongDan } from "./router";
import { RaoCau } from "./RaoCau";
import { ThanhTren } from "./ThanhTren";

const khoaLuuNhan = (userId: string) => "khsx.nhan." + userId;
function docNhanDaLuu(userId: string): string | null {
  try { return sessionStorage.getItem(khoaLuuNhan(userId)); } catch { return null; }
}
function luuNhan(userId: string, n: string) {
  try { sessionStorage.setItem(khoaLuuNhan(userId), n); } catch { /* trình duyệt chặn lưu ⇒ chỉ giữ trong RAM */ }
}

export function VoApp({ appUser, userId }: { appUser: AppUser; userId: string }) {
  const duongDan = useDuongDan();
  // Nền tải NGAY khi vào app (kể cả khi màn đang mở chưa cần) ⇒ chuyển màn là có dữ liệu.
  const nen = useNen(userId);
  // Tab đang hiện ⇒ mỗi 90 giây hỏi máy chủ có dữ liệu mới không (0350) ⇒ thấy người khác sửa mà không cần bấm vào cửa sổ
  useTuCapNhatNen(userId);
  const dsNhan = useMemo(() => bung<DongNhan>(nen.data?.bang.nhan), [nen.data?.bang.nhan]);
  const doTuoi = useMemo(() => docDoTuoi(docCauHinh(nen.data, "do_tuoi", null)), [nen.data]);
  const trangThaiNen = nen.isError ? (nen.data ? "cu" : "loi") : null;
  // Đo thời gian (⏱): chỉ khi Admin bật ở Cài đặt › Cấu hình (0348 — mặc định TẮT; DB cũng chặn ghi khi tắt). Tắt ⇒ ẩn nút ⏱, không gửi.
  const doTg = docCauHinh<boolean>(nen.data, "do_thoi_gian_bat", false) === true;
  useEffect(() => {
    if (!doTg) return;
    // Đợi vài giây cho LCP chốt rồi mới gửi số đo (gửi phần chưa gửi — bật giữa chừng thì gửi luôn số đo từ lúc mở trang).
    const hen = setTimeout(() => void guiDiemDoLenServer(__APP_VERSION__), 3000);
    return () => clearTimeout(hen);
  }, [doTg]);

  // null = chưa có danh sách nhãn ⇒ tạm theo nhãn đã lưu / mặc định, chốt lại khi danh sách về.
  const [nhanChon, setNhanChon] = useState<ChonNhan | null>(null);
  const nhan: ChonNhan = useMemo(() => {
    const coSan = nhanChon ?? docNhanDaLuu(userId);
    // Danh sách nhãn chưa về (lần đầu, chưa có cache) ⇒ tin tạm lựa chọn đã có / nhãn mặc định, kiểm lại khi danh sách về.
    if (!dsNhan.length) return coSan ?? appUser.nhan_mac_dinh ?? TAT_CA;
    return chonNhanKhoiDau(coSan, appUser.nhan_mac_dinh, dsNhan.map((n) => n.ma));
  }, [nhanChon, dsNhan, userId, appUser.nhan_mac_dinh]);

  // Số đỏ trên menu (QĐ-H78): ca Tiếp nhận đã qua ngày — tính từ gói nền, đổi ngay khi làn C vá cache.
  const homNay = homNayVN(Date.now());
  // (F) Tác vụ trễ: cần engine cho mọi lớp có chuẩn ⇒ tính SAU lượt vẽ (không chặn màn đầu); kết quả nhớ ở core/tacVuNen nên màn
  // Tác vụ mở sau đó dùng lại, không tính lần 2.
  const [treTacVu, setTreTacVu] = useState(0);
  useEffect(() => {
    const g = nen.data;
    if (!g?.bang.chuan_tac_vu?.rows.length) { setTreTacVu(0); return; }
    let huy = false;
    // Tải lười: engine + luật tác vụ không vào gói JS ban đầu (giới hạn 200 KB — SKILL §5.6).
    const t = setTimeout(() => {
      void Promise.all([import("../core/tacVuNen"), import("../core/tacVu")]).then(([a, b]) => {
        if (huy) return;
        const t0 = performance.now();
        const tv = a.tacVuTuNen(g);
        setTreTacVu(b.demTreTheoNhan(tv.viec, tv.homNay, tv.dauCuaSo, (n) => hopNhan(nhan, n)));
        ghiDiemDo({ man_hinh: "tacvu", buoc: "so_do_menu", ms: performance.now() - t0, so_dong: tv.viec.length });
      });
    }, 0);
    return () => { huy = true; clearTimeout(t); };
  }, [nen.data, nhan]);
  const soDo = useMemo(
    () => soDoMenu(bung<{ id: string; nhan: string | null }>(nen.data?.bang.lop), bung<{ lop_id: string; ngay: string }>(nen.data?.bang.nghi_cho), nhan, homNay, treTacVu),
    [nen.data?.bang.lop, nen.data?.bang.nghi_cho, nhan, homNay, treTacVu],
  );

  const chonNhan = useCallback((n: ChonNhan) => { setNhanChon(n); luuNhan(userId, n); }, [userId]);

  const chonMan = useCallback((m: KhoaMenu) => {
    const t0 = performance.now();
    dieuHuong({ man: m, con: [] });
    requestAnimationFrame(() => setTimeout(() => ghiDiemDo({ man_hinh: m, buoc: "chuyen_man", ms: performance.now() - t0 }), 0));
  }, []);
  const doiCon = useCallback((con: string[], thay?: boolean) => dieuHuong({ man: duongDan.man, con }, thay), [duongDan.man]);

  // Màn đầu đã hiện ⇒ tải trước mã các màn khác lúc rảnh.
  useEffect(() => {
    const t = setTimeout(() => taiTruocCacMan(duongDan.man), 3000);
    return () => clearTimeout(t);
    // chỉ chạy 1 lần sau khi vào app
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Man = MAN[duongDan.man];
  return (
    <div className="app">
      <ThanhTren
        appUser={appUser}
        manHienTai={duongDan.man}
        onChonMan={chonMan}
        nhan={nhan}
        dsNhan={dsNhan}
        onChonNhan={chonNhan}
        doTuoi={doTuoi}
        soDo={soDo}
        trangThaiNen={trangThaiNen}
        onThuLaiNen={() => void nen.refetch()}
      />
      {/* 0358: cầu đồng bộ lỗi / không chạy ⇒ dải đỏ cho Admin trên mọi màn */}
      {laAdmin(appUser) && <RaoLoi im><RaoCau userId={userId} dangODongBo={duongDan.man === "caidat" && duongDan.con[0] === "dong-bo"} /></RaoLoi>}
      <RaoLoi khoaReset={duongDan.man}>
        <Suspense fallback={<div className="app-cho">Đang tải màn…</div>}>
          <Man appUser={appUser} userId={userId} nhan={nhan} con={duongDan.con} doiCon={doiCon} />
        </Suspense>
      </RaoLoi>
      <Toasts />
      {doTg && <PerfPanel />}
    </div>
  );
}
